import React, {
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { View, TextInput, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { createPinInputStyles } from '../../../../shared/components/ui/PinInput/PinInput.styles';
import { PinInputProps } from '../../../../shared/components/ui/PinInput/PinInputProps';
import { useScreenTheme } from '../../../../shared/hooks/useScreenTheme';
import { regexExpressions } from '../../../../shared/constants/validations';

const PinInput = forwardRef<any, PinInputProps>(
  (
    {
      pinLength,
      onPinChanged,
      containerStyle,
      secureTextEntry = false,
      isInputInvalid = false,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const theme = useScreenTheme('pinAuthentication' as any);
    const styles = useMemo(() => createPinInputStyles(theme as any), [theme]);
    const [pin, setPin] = useState<string[]>(Array(pinLength).fill(''));
    const [focusedIndex, setFocusedIndex] = useState<number>(0);
    const lastFocusedIndex = useRef<number>(0);
    const inputs = useRef<Array<TextInput | null>>([]);

    // Returns index of first empty slot, or last slot if all filled
    const getActiveIndex = (currentPin: string[]) => {
      const firstEmpty = currentPin.findIndex(v => v === '');
      return firstEmpty === -1 ? pinLength - 1 : firstEmpty;
    };

    const handlePinChange = (index: number, value: string) => {
      const numericValue = value.replace(regexExpressions?.numbers, '');
      if (value && !numericValue) {
        return;
      }
      const newPin = [...pin];
      newPin[index] = numericValue;
      setPin(newPin);
      onPinChanged(newPin.join(''));
      if (numericValue && index < pinLength - 1) {
        inputs.current[index + 1]?.focus();
      }
    };

    const handleBackspace = (index: number, event: any) => {
      if (event.nativeEvent.key === 'Backspace') {
        const newPin = [...pin];
        const targetIndex = index ?? lastFocusedIndex.current;
        if (pin[targetIndex]) {
          // If the target input has a value, clear it.
          newPin[targetIndex] = '';
          setPin(newPin);
          onPinChanged(newPin.join(''));
          // Explicitly re-focus to make the cursor reappear.
          inputs.current[targetIndex]?.focus();
        } else if (targetIndex > 0) {
          // If the target input is empty, move to the previous one and clear it.
          const prevIndex = targetIndex - 1;
          newPin[prevIndex] = '';
          setPin(newPin);
          onPinChanged(newPin.join(''));
          inputs.current[prevIndex]?.focus();
        }
      }
    };

    // Programmatic input from external numpad
    const inputDigit = (digit: string) => {
      const currentPin = pin;
      const activeIdx = getActiveIndex(currentPin);
      if (currentPin[activeIdx] !== '') {
        // All slots filled — ignore
        return;
      }
      const newPin = [...currentPin];
      newPin[activeIdx] = digit;
      setPin(newPin);
      onPinChanged(newPin.join(''));
      if (activeIdx < pinLength - 1) {
        inputs.current[activeIdx + 1]?.focus();
      }
    };

    const deleteDigit = () => {
      // Find the last filled slot from the current pin state.
      // This avoids relying on lastFocusedIndex.current which can be stale on
      // web because clicking the numpad button steals focus from the TextInput,
      // causing onFocus to fire asynchronously and leaving lastFocusedIndex out
      // of sync between successive delete presses.
      const currentPin = pin;
      const lastFilledIndex = currentPin.reduce(
        (acc, v, i) => (v !== '' ? i : acc),
        -1,
      );
      const targetIndex = lastFilledIndex !== -1 ? lastFilledIndex : 0;
      handleBackspace(targetIndex, { nativeEvent: { key: 'Backspace' } });
    };

    // expose methods to parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputs.current[0]?.focus();
      },
      clear: () => {
        const emptyPin = Array(pinLength).fill('');
        setPin(emptyPin);
        onPinChanged(emptyPin.join(''));
        if (Platform.OS !== 'web') {
          inputs.current[0]?.focus();
        }
      },
      inputDigit,
      deleteDigit,
    }));

    return (
      <View {...rest} style={[styles.container, containerStyle]}>
        {Array(pinLength)
          .fill(0)
          .map((_, index) => (
            <TextInput
              key={index}
              testID={`${rest.testID}-hidden-input${index}`}
              accessibilityLabel={`${rest.accessibilityLabel ?? 'PIN'} digit ${index + 1} of ${pinLength}`}
              accessibilityHint={pin[index] ? t('pinAuthentication.accessibility.filled') : t('pinAuthentication.accessibility.empty')}
              style={[
                styles.input,
                pin[index] !== '' && styles.inputBackground,
                isInputInvalid && styles.errorInput,
                !isInputInvalid && focusedIndex === index && styles.focusedInput,
              ]}
              keyboardType="number-pad"
              showSoftInputOnFocus={true}
              maxLength={1}
              onChangeText={value => handlePinChange(index, value)}
              onKeyPress={event => handleBackspace(index, event)}
              onFocus={() => {
                setFocusedIndex(index);
                lastFocusedIndex.current = index;
              }}
              onBlur={() => setFocusedIndex(-1)}
              ref={input => {
                inputs.current[index] = input;
              }}
              value={secureTextEntry && pin[index] ? '•' : pin[index]}
              secureTextEntry={false}
              cursorColor={theme.pinInputText}
            />
          ))}
      </View>
    );
  },
);

export default PinInput;
